import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useFormikContext } from 'formik';
import { FieldInput } from 'motion-components';
import { gqlGetPostalcode2 } from '../graphql';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';

type Option = {
  id: number;
  postalcode: string;
  area: string | null;
  city: string | null;
  county: string | null;
  country: string | null;
  regionId: number | null;
};

type Props = {
  name?: string;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  minChars?: number;
  limit?: number;
};

export const PostalcodeSearchInput: React.FC<Props> = ({
  name = 'postalcode',
  label = 'Τ.Κ.',
  disabled = false,
  required = false,
  minChars = 3,
  limit = 50,
}) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const [input, setInput] = useState<string>(() => String(values?.[name] ?? ''));
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Option[]>([]);
  const [focused, setFocused] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const [runSearch, { data, loading }] = useLazyQuery(gqlGetPostalcode2, {
    fetchPolicy: 'network-only',
  });

  // keep local input in sync with Formik (reinit etc.)
  useEffect(() => {
    setInput(String(values?.[name] ?? ''));
  }, [values?.[name]]); // eslint-disable-line react-hooks/exhaustive-deps

  // build where
  const where = useMemo(() => {
    if (!input || input.length < minChars) return null;
    return JSON.stringify({
      $or: [
        { postalcode: { $like: `%${input}%` } },
        { area: { $like: `%${input}%` } },
        { city: { $like: `%${input}%` } },
      ],
      isActive: true,
    });
  }, [input, minChars]);

  // Debounced search
  useEffect(() => {
    if (!where) {
      setOptions([]);
      setOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch({ variables: { limit, where, order: 'postalcode ASC' } });
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [where, runSearch, limit]);

  // map results -> options; show only when focused
  useEffect(() => {
    const rows: Option[] =
      (data?.getPostalcode2Fotokiklosi?.data ?? []).map((r: any) => ({
        id: r.id,
        postalcode: r.postalcode,
        area: r.area ?? null,
        city: r.city ?? null,
        county: r.county ?? null,
        country: r.country ?? null,
        regionId: r.regionId ?? null,
      })) ?? [];
    setOptions(rows);
    setOpen(focused && rows.length > 0);
  }, [data, focused]);

  // if user focuses after data already loaded, open immediately
  useEffect(() => {
    if (focused && options.length > 0) setOpen(true);
  }, [focused, options.length]);

  // outside click closes
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      setInput(v);
      setFieldValue(name, v);
      // don’t auto-open until minChars
      if (v.length < minChars) setOpen(false);
    },
    [name, minChars, setFieldValue],
  );

  const handlePick = useCallback(
    (opt: Option) => {
      setFieldValue(name, opt.postalcode);
      setFieldValue('area', opt.area ?? '');
      setFieldValue('city', opt.city ?? '');
      setFieldValue('county', opt.county ?? '');
      setFieldValue('country', opt.country ?? '');
      if (opt.regionId != null) setFieldValue('regionId', opt.regionId);
      setInput(opt.postalcode);
      setOpen(false);
    },
    [name, setFieldValue],
  );

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative' }}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={() => {
        setFocused(false);
        setOpen(false);
      }}
    >
      <FieldInput
        name={name}
        label={label}
        disabled={disabled}
        required={required}
        // FieldInput is Formik-bound, but we still mirror local input for debounce/search
        onChange={handleChange}
        placeholder="Αναζήτηση Τ.Κ., περιοχή ή πόλη…"
      />
      {input.length > 0 && input.length < minChars && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: '#fff',
            border: '1px solid #dee2e6',
            borderTop: 'none',
            padding: '6px 8px',
            fontSize: 12,
            color: '#6c757d',
            zIndex: 20,
          }}
        >
          {t('Πληκτρολογήστε τουλάχιστον')} {minChars} {t('χαρακτήρες')}
        </div>
      )}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: 240,
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid #dee2e6',
            borderTop: 'none',
            zIndex: 30,
          }}
        >
          {loading && (
            <div style={{ padding: '6px 8px', fontSize: 12, color: '#6c757d' }}>
              {t('Φόρτωση…')}
            </div>
          )}
          {!loading &&
            options.map((o) => (
              <div
                key={o.id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handlePick(o)}
                style={{
                  padding: '6px 8px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f1f3f5',
                }}
              >
                <div style={{ fontWeight: 600 }}>{o.postalcode}</div>
                <div style={{ fontSize: 12, color: '#6c757d' }}>
                  {o.area ?? '-'}
                  {o.city ? `, ${o.city}` : ''}
                  {o.county ? ` (${o.county})` : ''}
                </div>
              </div>
            ))}
          {!loading && options.length === 0 && (
            <div style={{ padding: '6px 8px', fontSize: 12, color: '#6c757d' }}>
              {t('Δεν βρέθηκαν αποτελέσματα')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
